import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const inputClass = "bg-slate-900 border border-slate-700 rounded p-2 text-white w-full";
const money = v => (v / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const display = (v, unit) => unit === "money" ? money(v) : v.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
function parseAmount(text, unit) {
  const s = String(text).trim();
  if (!(unit === "money" ? /^\d+(?:[,.]\d{1,2})?$/ : /^\d+$/).test(s)) throw new Error("Use valores positivos, sem separador de milhar. Exemplo: 1500,50.");
  const value = Number(s.replace(",", "."));
  const scaled = Math.round(value * (unit === "money" ? 100 : 1));
  if (!Number.isSafeInteger(scaled) || scaled > 100000000000) throw new Error("Valor acima do limite permitido.");
  return scaled;
}
// Feriados da capital; pontos facultativos e emendas não entram.
function saoPauloHolidays(y) {
  const dates = new Set(["01-01", "01-25", "04-21", "05-01", "07-09", "09-07", "10-12", "11-02", "11-15", "11-20", "12-25"].map(d => y + "-" + d));
  // Páscoa gregoriana: base para Sexta-feira Santa e Corpus Christi.
  const a = y % 19, b = Math.floor(y / 100), c = y % 100;
  const f = Math.floor((b + 8) / 25);
  const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - f + 1) / 3) + 15) % 30;
  const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - c % 4) % 7;
  const n = h + l - 7 * Math.floor((a + 11 * h + 22 * l) / 451) + 114;
  for (const offset of [-2, 60]) {
    dates.add(new Date(Date.UTC(y, Math.floor(n / 31) - 1, n % 31 + 1 + offset)).toISOString().slice(0, 10));
  }
  return dates;
}

function weekdays(month, today) {
  const [y, m] = month.split("-").map(Number);
  const holidays = saoPauloHolidays(y);
  let total = 0, passed = 0, remaining = 0, todayIsWorkday = false;
  for (let d = 1; d <= new Date(y, m, 0).getDate(); d++) {
    const day = new Date(y, m - 1, d);
    const key = `${month}-${String(d).padStart(2, "0")}`;
    if (day.getDay() !== 0 && day.getDay() !== 6 && !holidays.has(key)) {
      if (key === today) todayIsWorkday = true;
      total++;
      if (key <= today) passed++;
      if (key >= today) remaining++;
    }
  }
  return { total, passed, remaining, todayIsWorkday };
}
function SalesRanking({ data, month }) {
  const [ranking, setRanking] = useState(null);
  const [failed, setFailed] = useState(false);
  const [sound, setSound] = useState(false);
  const [celebration, setCelebration] = useState("");
  const audio = useRef(null);
  const previous = useRef(null);
  useEffect(() => {
    let active = true, busy = false;
    const refresh = async () => {
      if (busy) return;
      busy = true;
      try {
        const { data: result } = await api.get("/sales-ranking", {params: {month}});
        if (active) { setRanking(result); setFailed(false); }
      } catch { if (active) setFailed(true); }
      finally { busy = false; }
    };
    refresh();
    const timer = setInterval(refresh, 10000);
    return () => { active = false; clearInterval(timer); audio.current?.close().catch(() => {}); audio.current = null; };
  }, [month]);
  function chime() {
    const ctx = audio.current;
    if (!ctx || ctx.state !== "running") return;
    [523, 659, 784].forEach((hz, i) => {
      const at = ctx.currentTime + i * 0.105;
      const tone = ctx.createOscillator(), volume = ctx.createGain();
      tone.frequency.value = hz;
      volume.gain.setValueAtTime(0.065, at);
      volume.gain.exponentialRampToValueAtTime(0.001, at + 0.18);
      tone.connect(volume); volume.connect(ctx.destination);
      tone.start(at); tone.stop(at + 0.19);
    });
  }
  async function toggleSound() {
    if (sound) { setSound(false); return; }
    try {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) return;
      audio.current ||= new Audio();
      await audio.current.resume();
      setSound(audio.current.state === "running"); chime();
    } catch { setSound(false); }
  }
  const [product, setProduct] = useState("");
  const selected = data.products.find(p => p.id === product);
  const products = selected ? [selected] : data.products.filter(p => p.unit === "money");
  const rows = (ranking?.team || []).map(person => ({
    ...person,
    total: products.reduce((sum, p) => sum + (ranking.by_user[person.id]?.[p.id] || 0), 0)
  })).filter(person => person.total > 0).sort((a,b) => b.total - a.total || a.name.localeCompare(b.name, "pt-BR"));
  useEffect(() => {
    if (!ranking) return;
    const prior = previous.current;
    previous.current = { product, totals: Object.fromEntries(rows.map(p => [p.id,p.total])) };
    if (!prior || prior.product !== product) return;
    const overtaker = rows.find(a => rows.some(b => a.id !== b.id &&
      (prior.totals[a.id] || 0) <= (prior.totals[b.id] || 0) &&
      (prior.totals[b.id] || 0) > 0 &&
      a.total > b.total && a.total > (prior.totals[a.id] || 0)));
    if (overtaker) {
      setCelebration("🎉 " + overtaker.name + " subiu no ranking!");
      if (sound) chime();
    }
  });
  useEffect(() => {
    if (!celebration) return;
    const timer = setTimeout(() => setCelebration(""), 4500);
    return () => clearTimeout(timer);
  }, [celebration]);
  return <section className="rounded-xl border border-pink-500/30 bg-slate-900 p-4 space-y-3">
    <div className="flex flex-wrap justify-between items-center gap-2">
      <div><h3 className="font-bold text-pink-300">🏆 Ranking de vendas</h3>
      <p className="text-xs text-slate-400">Produção registrada no mês selecionado.</p></div>
      <Button type="button" onClick={toggleSound} aria-pressed={sound}>{sound ? "🔊 Som ligado" : "🔇 Ativar som"}</Button>
      <select aria-label="Produto do ranking" className={inputClass} value={product} onChange={e => setProduct(e.target.value)}>
        <option value="">Total em R$</option>
        {data.products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </div>
    {celebration && <p role="status" className="flex items-center gap-2 rounded-lg bg-pink-500/20 p-2 text-pink-200">
      <style>{`
        @keyframes ranking-cat-hop {
          0%, 50%, 100% { transform: translateY(0); }
          25%, 75% { transform: translateY(-7px) rotate(-8deg); }
        }
        .ranking-cat-hop { animation: ranking-cat-hop 1.9s ease-in-out both; }
        @media (prefers-reduced-motion: reduce) {
          .ranking-cat-hop { animation: none; }
        }
      `}</style>
      <svg className="ranking-cat-hop" aria-hidden="true" width="44" height="36" viewBox="0 0 44 36">
        <path d="M10 20 10 6l9 7h6l9-7v14c0 8-5 12-12 12s-12-4-12-12Z" fill="#f472b6" stroke="#c084fc" strokeWidth="2"/>
        <circle cx="17" cy="21" r="1.5" fill="#301442"/>
        <circle cx="27" cy="21" r="1.5" fill="#301442"/>
        <path d="M19 25q1.5 3 3 0 1.5 3 3 0M2 15l3 2m33 10 4-2M20 5l1-3" fill="none" stroke="#fde68a" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
      {celebration}
    </p>}
    {failed && <p role="status" className="text-xs text-amber-300">Não foi possível atualizar. Tentaremos novamente.</p>}
    {!ranking ? <p>Carregando ranking…</p> : rows.length ? <ol className="space-y-2">{rows.map(person => {
      const position = rows.findIndex(row => row.total === person.total) + 1;
      return <li key={person.id} className={`flex items-center gap-3 rounded-lg border p-3 ${position === 1 ? "border-pink-500/40 bg-pink-500/10" : "border-slate-700"}`}>
        <span className="font-bold text-pink-300">{position}º</span><span className="flex-1">{person.name}</span>
        <strong>{display(person.total, selected?.unit || "money")}</strong>
      </li>;
    })}</ol> : <p className="text-sm text-slate-400">Nenhuma venda registrada nesse recorte.</p>}
    {!selected && <p className="text-xs text-slate-400">Seguro e Super Conta aparecem nos rankings por produto.</p>}
  </section>;
}

function GoalsTable({ products, targets, produced, days, onAdd, onCorrect }) {
  const rows = products.map(p => ({ ...p, target: targets[p.id] || 0, produced: produced[p.id] || 0 }));
  rows.push({ id: "total", name: "Total em R$", unit: "money", target: rows.filter(p => p.unit === "money").reduce((n,p) => n + p.target, 0), produced: rows.filter(p => p.unit === "money").reduce((n,p) => n + p.produced, 0) });
  return <div className="overflow-x-auto"><table className="w-full text-sm text-right whitespace-nowrap"><thead><tr className="text-pink-400 border-b border-slate-700">{["Produto", "Meta mensal", "Produzido", "Falta", "Diária necessária", "Projeção", "Projeção %"].map(h => <th key={h} className="p-3">{h}</th>)}</tr></thead><tbody>{rows.map(p => {
    const left = Math.max(0, p.target - p.produced);
    const projection = days.passed ? p.produced / days.passed * days.total : null;
    return <tr key={p.id} className={`border-b border-slate-800 ${p.id === "total" ? "font-bold text-pink-300" : ""}`}><td className="p-3 text-left">{p.name}</td><td className="p-3">{display(p.target,p.unit)}</td><td className="p-3">{display(p.produced,p.unit)} {onCorrect && p.id !== "total" && <button type="button" className="ml-2 text-pink-300 underline" onClick={() => onCorrect(p.id)}>Corrigir</button>} {onAdd && p.id !== "total" && <button type="button" onClick={() => onAdd(p.id)} className="ml-2 text-pink-400 underline" aria-label={`Adicionar venda em ${p.name}`}>+ adicionar</button>}</td><td className="p-3">{display(left,p.unit)}</td><td className="p-3">{days.remaining ? display(p.unit === "count" ? Math.ceil(left/days.remaining) : left/days.remaining,p.unit) : left ? "Mês encerrado" : "Atingida"}</td><td className="p-3">{projection === null ? "—" : display(projection,p.unit)}</td><td className="p-3">{projection !== null && p.target ? `${(projection/p.target*100).toFixed(1)}%` : "—"}</td></tr>;
  })}</tbody></table></div>;
}
export default function SalesGoals() {
  const [month,setMonth] = useState(() => new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" }).format(new Date()));
  const [data,setData] = useState(null), [error,setError] = useState(""), [busy,setBusy] = useState(false), [loading,setLoading] = useState(true);
  const [editor,setEditor] = useState(false), [targets,setTargets] = useState({}), [participants,setParticipants] = useState([]);
  const [correction, setCorrection] = useState(null);
  const [sale,setSale] = useState(null), [success,setSuccess] = useState("");
  const [individualEditor, setIndividualEditor] = useState(null);
  const [individualDraft, setIndividualDraft] = useState({});
  const individualForm = useRef(null);
  useEffect(() => { if (individualEditor) individualForm.current?.scrollIntoView({ block: "start", behavior: "smooth" }); }, [individualEditor]);
  const lock = useRef(false), generation = useRef(0);
  const load = useCallback(async () => {
    const id = ++generation.current;
    setLoading(true); setError("");
    try { const r = await api.get("/sales-goals", { params: { month } }); if (id === generation.current) setData(r.data); }
    catch(e) { if (id === generation.current) { setData(null); setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Não foi possível carregar as metas."); } }
    finally { if (id === generation.current) setLoading(false); }
  },[month]);
  useEffect(() => { load(); return () => { generation.current++; }; },[load]);
  async function perform(action, message) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(""); setSuccess("");
    try { await action(); setEditor(false); setSale(null); setIndividualEditor(null); await load(); setSuccess(message); }
    catch(e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : e.response ? "Confira os campos e tente novamente." : e.message); }
    finally { lock.current = false; setBusy(false); }
  }
  function editIndividualGoal(userId) {
    const values = data.individual_targets[userId] || {};
    setIndividualDraft(Object.fromEntries(data.products.map(p => [p.id, String((values[p.id] || 0) / (p.unit === "money" ? 100 : 1))])));
    setIndividualEditor({ userId, version: data.individual_versions[userId] || 0 });
    setEditor(false); setSale(null); setError(""); setSuccess("");
  }
  function editGoals() {
    setIndividualEditor(null);
    setTargets(Object.fromEntries(data.products.map(p => [p.id, String((data.targets[p.id] || 0)/(p.unit === "money" ? 100 : 1))])));
    setParticipants(data.participant_ids.filter(id => data.team.some(t => t.id === id))); setEditor(true); setSale(null); setSuccess("");
  }
  function startSale(entry, product) {
    setIndividualEditor(null);
    setEditor(false); setSuccess("");
    setSale(entry ? { ...entry, amount: String(entry.amount/(data.products.find(p => p.id === entry.product).unit === "money" ? 100 : 1)) } : { user_id: data.manager ? "" : data.self_id, product: product || "cnc", date: month === data.today.slice(0,7) ? data.today : month + "-01", amount: "", version: 0, request_id: crypto.randomUUID() });
  }
  const days = data ? weekdays(month,data.today) : null;
  const names = { ...Object.fromEntries((data?.team || []).map(t => [t.id,t.name])), ...(data ? { [data.self_id]: data.self_name || "Minha meta" } : {}) };
  const available = data?.team.filter(t => data.participant_ids.includes(t.id)) || [];
  
  function downloadExcel() {
    const rows = [["Mês",month], ["Produto","Meta mensal","Produzido","Falta","Diária necessária","Projeção","Projeção %"]];
    data.products.forEach(p => { const target=data.targets[p.id]||0, actual=data.totals[p.id]||0, left=Math.max(0,target-actual), scale=p.unit === "money" ? 100 : 1, projected=days.passed ? actual/days.passed*days.total : null; rows.push([p.name,target/scale,actual/scale,left/scale,days.remaining ? (p.unit === "count" ? Math.ceil(left/days.remaining) : left/days.remaining/scale) : "",projected === null ? "" : projected/scale,projected !== null && target ? projected/target*100 : ""]); });
    if (data.manager) {
      rows.push([], ["Metas individuais"], ["Pessoa", "Produto", "Meta individual", "Produzido", "Falta", "Diária necessária", "Projeção", "Projeção %"]);
      data.individual_user_ids.forEach(id => data.products.forEach(p => {
        const target = data.individual_targets[id]?.[p.id] || 0, actual = data.by_user[id]?.[p.id] || 0;
        const left = Math.max(0, target - actual), scale = p.unit === "money" ? 100 : 1;
        const projected = days.passed ? actual / days.passed * days.total : null;
        rows.push([names[id] || "Consultora", p.name, target / scale, actual / scale, left / scale,
          days.remaining ? (p.unit === "count" ? Math.ceil(left / days.remaining) : left / days.remaining / scale) : "",
          projected === null ? "" : projected / scale, projected !== null && target ? projected / target * 100 : ""]);
      }));
    }
    rows.push([], ["Data","Consultora","Produto","Valor em R$ ou quantidade"]);
    data.entries.forEach(e => { const p=data.products.find(x=>x.id===e.product); rows.push([e.date,names[e.user_id]||"Consultora removida",p.name,e.amount/(p.unit === "money" ? 100 : 1)]); });
    const escape = v => { const s=typeof v === "number" ? (Number.isFinite(v) ? v.toFixed(2).replace(".", ",") : "") : String(v); const safe=typeof v === "string" && /^[\s]*[=+@\-]/.test(s) ? "'"+s : s; return `"${safe.replace(/"/g,'""')}"`; };
    const csv="\ufeff"+rows.map(row=>row.map(escape).join(";")).join("\r\n");
    const url=URL.createObjectURL(new Blob([csv],{type:"text/csv;charset=utf-8"})); const a=document.createElement("a"); a.href=url; a.download=`metas-vendas-${month}.csv`; a.click(); setTimeout(()=>URL.revokeObjectURL(url),1000);
  }
  function printReport() {
    const popup=window.open("", "_blank"); if(!popup){setError("Permita janelas pop-up para salvar o relatório em PDF.");return;}
    const clean=v=>String(v).replace(/[&<>"']/g,c=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;", "'":"&#39;"}[c]));
    const lines=data.products.map(p=>{const target=data.targets[p.id]||0, actual=data.totals[p.id]||0, left=Math.max(0,target-actual), projection=days.passed?actual/days.passed*days.total:null;return `<tr><td>${clean(p.name)}</td><td>${clean(display(target,p.unit))}</td><td>${clean(display(actual,p.unit))}</td><td>${clean(display(left,p.unit))}</td><td>${days.remaining?clean(display(p.unit==="count"?Math.ceil(left/days.remaining):left/days.remaining,p.unit)):"—"}</td><td>${projection===null?"—":clean(display(projection,p.unit))}</td></tr>`}).join("");
    const individualReport = data.manager ? data.individual_user_ids.map(id => {
      const lines = data.products.map(p => {
        const target = data.individual_targets[id]?.[p.id] || 0, actual = data.by_user[id]?.[p.id] || 0;
        const left = Math.max(0, target - actual), projected = days.passed ? actual / days.passed * days.total : null;
        return `<tr><td>${clean(p.name)}</td><td>${clean(display(target, p.unit))}</td><td>${clean(display(actual, p.unit))}</td><td>${clean(display(left, p.unit))}</td><td>${days.remaining ? clean(display(p.unit === "count" ? Math.ceil(left / days.remaining) : left / days.remaining, p.unit)) : "—"}</td><td>${projected === null ? "—" : clean(display(projected, p.unit))}</td></tr>`;
      }).join("");
      return `<h2>Meta individual — ${clean(names[id] || "Consultora")}</h2><table><thead><tr><th>Produto</th><th>Meta</th><th>Produzido</th><th>Falta</th><th>Diária</th><th>Projeção</th></tr></thead><tbody>${lines}</tbody></table>`;
    }).join("") : "";
    const entries=data.entries.map(e=>{const p=data.products.find(x=>x.id===e.product);return `<tr><td>${clean(e.date)}</td><td>${clean(names[e.user_id]||"Consultora removida")}</td><td>${clean(p.name)}</td><td>${clean(display(e.amount,p.unit))}</td></tr>`}).join("");
    popup.document.write(`<!doctype html><html lang="pt-BR"><head><title>Metas e vendas ${clean(month)}</title><style>body{font:12px Arial;padding:20px}h1{font-size:20px}table{border-collapse:collapse;width:100%;margin:12px 0 24px}th,td{border:1px solid #999;padding:6px;text-align:left}th{background:#eee}@page{size:landscape;margin:12mm}tr{break-inside:avoid}</style></head><body><h1>Metas e vendas — ${clean(month)}</h1><p>Dias úteis: ${days.total} · até hoje: ${days.passed} · restantes incluindo hoje: ${days.remaining}. Feriados não descontados.</p><table><thead><tr><th>Produto</th><th>Meta</th><th>Produzido</th><th>Falta</th><th>Diária</th><th>Projeção</th></tr></thead><tbody>${lines}</tbody></table>${individualReport}<h2>Lançamentos</h2><table><thead><tr><th>Data</th><th>Consultora</th><th>Produto</th><th>Produção</th></tr></thead><tbody>${entries}</tbody></table></body></html>`); popup.document.close(); popup.focus(); popup.print();
  }
  return <Card className="bg-[#111827] border-slate-800 p-5 space-y-5">
    <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-2xl font-bold">Metas e produção</h2><p className="text-sm text-slate-400">{data?.manager ? "Meta da loja e acompanhamento por consultora." : "Acompanhe sua meta e produção."}</p></div><label className="text-sm">Mês<input aria-label="Mês das metas" type="month" min="2020-01" max="2100-12" value={month} disabled={busy} onChange={e => { if(e.target.value) { setMonth(e.target.value); setEditor(false); setSale(null); setIndividualEditor(null); setSuccess(""); } }} className={inputClass}/></label></div>
    {error && <p role="alert" className="text-red-300">{error}</p>}{success && <p role="status" className="text-emerald-300">{success}</p>}
    {loading ? <p>Carregando metas…</p> : data ? <>
      {month === data.today.slice(0, 7) && <section className="rounded-xl border border-pink-500/30 p-4">
        <h3 className="font-semibold text-pink-300">{data.manager ? "Produção de hoje" : "Minha produção de hoje"}</h3>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <TodayProduction remaining={days.remaining} todayIsWorkday={days.todayIsWorkday} name={data.manager ? "Loja" : "Minha produção"} data={data} targets={data.targets} totals={data.totals}/>
          {data.manager && data.individual_user_ids.filter(id => id !== data.self_id).map(id => <TodayProduction remaining={days.remaining} todayIsWorkday={days.todayIsWorkday} key={id} name={names[id] || "Consultora"} data={data} owner={id} targets={data.individual_targets[id]} totals={data.by_user[id]}/>)}
        </div>
      </section>}

      <div className="flex flex-wrap gap-3"><Button disabled={busy} onClick={() => { setIndividualEditor(null); setEditor(false); setSale(null); load(); }}>Atualizar</Button>{!data.manager && <Button disabled={busy} onClick={() => editIndividualGoal(data.self_id)}>Editar minha meta</Button>}<Button disabled={busy} onClick={downloadExcel}>Baixar planilha (Excel)</Button><Button disabled={busy} onClick={printReport}>Salvar em PDF</Button>{data.manager && <Button disabled={busy} onClick={editGoals}>Definir metas e divisão</Button>}<Button className="bg-pink-500 hover:bg-pink-600" disabled={busy || !available.length || (!data.manager && !data.participant_ids.includes(data.self_id))} onClick={() => startSale(null)}>Registrar venda</Button></div>
      <p className="text-sm text-slate-400">{days.total} dias úteis no mês · {days.passed} até hoje · {days.remaining} restantes, incluindo hoje. Feriados de São Paulo (capital) descontados.</p>
      {!data.participant_ids.length && <p>As metas deste mês ainda não foram definidas pela supervisora.</p>}
      {editor && <form className="space-y-4 border border-pink-500/40 rounded p-4" onSubmit={e => { e.preventDefault(); perform(async () => { const values = Object.fromEntries(data.products.map(p => [p.id,parseAmount(targets[p.id],p.unit)])); await api.put(`/sales-goals/${month}`, { targets: values, participant_ids: participants, version: data.version }); },"Metas salvas."); }}>
        <h3 className="font-bold">Meta mensal da loja</h3><div className="grid sm:grid-cols-2 gap-3">{data.products.map(p => <label key={p.id} className="text-sm">{p.name} ({p.unit === "money" ? "R$" : "quantidade"})<input required disabled={busy} inputMode="decimal" value={targets[p.id]} onChange={e => setTargets({...targets,[p.id]:e.target.value})} className={inputClass}/></label>)}</div>
        <fieldset disabled={busy}><legend>Dividir igualmente entre:</legend>{data.team.map(t => <label key={t.id} className="inline-flex items-center gap-2 mr-4 mt-2"><input type="checkbox" checked={participants.includes(t.id)} onChange={e => setParticipants(e.target.checked ? [...participants,t.id] : participants.filter(id => id !== t.id))}/>{t.name}</label>)}</fieldset><p className="text-xs text-slate-400">Centavos e unidades excedentes são distribuídos pela ordem da seleção. Essa divisão serve como meta inicial. As metas individuais editadas são mantidas e podem ter uma soma diferente da meta da loja.</p><Button disabled={busy || !participants.length} type="submit">{busy ? "Salvando…" : "Salvar metas"}</Button> <Button type="button" disabled={busy} onClick={() => setEditor(false)}>Cancelar</Button>
      </form>}
      {individualEditor && <form ref={individualForm} className="space-y-4 border border-pink-500/40 rounded p-4" onSubmit={e => {
        e.preventDefault();
        perform(async () => {
          const values = Object.fromEntries(data.products.map(p => [p.id, parseAmount(individualDraft[p.id], p.unit)]));
          await api.put(`/sales-goals/${month}/individual/${individualEditor.userId}`, { targets: values, version: individualEditor.version });
        }, "Meta individual salva.");
      }}>
        <h3 className="font-bold">Meta individual — {names[individualEditor.userId] || "Consultora"} — {month}</h3>
        <p className="text-sm text-slate-400">Esses valores alteram somente a meta desta pessoa no mês selecionado. A meta da loja permanece separada.</p>
        <div className="grid sm:grid-cols-2 gap-3">{data.products.map(p => <label key={p.id} className="text-sm">{p.name} ({p.unit === "money" ? "R$" : "quantidade"})
          <input required disabled={busy} inputMode={p.unit === "money" ? "decimal" : "numeric"} value={individualDraft[p.id] || ""}
            onChange={e => setIndividualDraft({ ...individualDraft, [p.id]: e.target.value })} className={inputClass} />
        </label>)}</div>
        <p className="text-xs text-slate-400">Digite sem separador de milhar, por exemplo: 1500,50. Use zero quando não houver meta para o produto.</p>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={busy}>{busy ? "Salvando…" : "Salvar meta individual"}</Button>
          <Button type="button" disabled={busy} onClick={() => setIndividualEditor(null)}>Cancelar</Button>
          {data.individual_custom[individualEditor.userId] && <Button type="button" disabled={busy} onClick={() => perform(
            () => api.put(`/sales-goals/${month}/individual/${individualEditor.userId}`, { targets: null, version: individualEditor.version }),
            "Meta individual voltou à divisão da loja.")}>Usar divisão da loja</Button>}
        </div>
      </form>}
      {correction && correction.month === month && <section className="rounded border border-slate-700 p-3">
        <strong>Escolha o lançamento para corrigir</strong>
        <button type="button" className="ml-3 underline" onClick={() => setCorrection(null)}>Fechar</button>
        {data.entries.filter(e => e.product === correction.product && (!correction.owner || e.user_id === correction.owner)).length === 0 && <p>Nenhum lançamento neste produto.</p>}
        {data.entries.filter(e => e.product === correction.product && (!correction.owner || e.user_id === correction.owner)).map(e => <div key={e.id} className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <span>{e.date.split("-").reverse().join("/")} · {names[e.user_id] || "Consultora"} · {display(e.amount, data.products.find(p => p.id === e.product).unit)}</span>
          <Button type="button" disabled={busy} onClick={() => { setCorrection(null); startSale(e); }}>Editar valor</Button>
          <Button type="button" variant="outline" className="text-rose-300" disabled={busy} onClick={() => {
            const amount = display(e.amount, data.products.find(p => p.id === e.product).unit);
            if (window.confirm(`Excluir definitivamente o lançamento de ${amount} de ${names[e.user_id] || "Consultora"}, em ${e.date.split("-").reverse().join("/") }? O valor será retirado do produzido.`)) {
              perform(() => api.delete(`/store-sales/${encodeURIComponent(e.id)}`, {params: {version: e.version}}), "Lançamento excluído.");
            }
          }}>Excluir</Button>
        </div>)}
      </section>}
      {sale && <form className="space-y-3 border border-pink-500/40 rounded p-4" onSubmit={e => { e.preventDefault(); perform(async () => { const amount = parseAmount(sale.amount,data.products.find(p => p.id === sale.product).unit); if (!amount) throw new Error("Informe um valor maior que zero."); const body = {user_id:sale.user_id,product:sale.product,date:sale.date,amount,version:sale.version,request_id:sale.request_id || crypto.randomUUID()}; if(sale.id) await api.put(`/store-sales/${sale.id}`,body); else await api.post("/store-sales",body); },"Produção salva."); }}>
        <h3 className="font-bold">{sale.id ? "Corrigir venda" : "Registrar venda"}</h3><div className="grid sm:grid-cols-2 gap-3"><label>Consultora<select required disabled={busy || !data.manager} value={sale.user_id} onChange={e => setSale({...sale,user_id:e.target.value})} className={inputClass}><option value="">Selecione</option>{available.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label><label>Produto<select disabled={busy} value={sale.product} onChange={e => setSale({...sale,product:e.target.value,amount:""})} className={inputClass}>{data.products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.unit === "money" ? "R$" : "quantidade"})</option>)}</select></label><label>Data<input required type="date" min={`${month}-01`} max={month === data.today.slice(0,7) ? data.today : `${month}-${new Date(Number(month.slice(0,4)),Number(month.slice(5)),0).getDate()}`} disabled={busy} value={sale.date} onChange={e => setSale({...sale,date:e.target.value})} className={inputClass}/></label><label>Valor ou quantidade<input required inputMode="decimal" disabled={busy} value={sale.amount} onChange={e => setSale({...sale,amount:e.target.value})} placeholder="Ex.: 1500,50" className={inputClass}/></label></div><p className="text-xs text-slate-400">Registre cada venda uma vez. Para ajustar um lançamento existente, use Corrigir.</p><Button type="submit" disabled={busy}>{busy ? "Salvando…" : "Salvar venda"}</Button> <Button type="button" disabled={busy} onClick={() => setSale(null)}>Cancelar</Button>
      </form>}
      <div><h3 className="font-bold mb-2">{data.manager ? "Resultado da loja" : "Minha meta e produção"}</h3>{data.manager && month === data.today.slice(0, 7) && days.todayIsWorkday && <StoreFocus data={data} days={days}/>}<p className="text-xs text-slate-400">Use + adicionar para somar uma venda ao produzido.</p><GoalsTable products={data.products} targets={data.targets} produced={data.totals} days={days} onCorrect={product => { setSale(null); setCorrection({month, product}); }} onAdd={available.length ? id => startSale(null, id) : null}/></div>
      <section className="rounded-xl border border-pink-500/30 p-4">
        <h3 className="font-semibold text-pink-300">Ritmo da meta</h3>
        <p className="text-xs text-slate-400">{days.remaining} dias úteis restantes · calendário de São Paulo (capital).</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <PaceCard name={data.manager ? "Loja" : "Minha meta"} products={data.products} targets={data.targets} totals={data.totals} remaining={days.remaining}/>
          {data.manager && data.individual_user_ids.filter(id => id !== data.self_id).map(id => <PaceCard key={id} name={names[id] || "Consultora"} products={data.products} targets={data.individual_targets[id]} totals={data.by_user[id]} remaining={days.remaining}/>)}
        </div>
      </section>
      <DailyClosing data={data} month={month} names={names} />
      <CopyDailyTargets data={data} month={month} days={days} />
      {month === "2026-11" && month === data.today.slice(0, 7) && (data.manager
        ? <SupervisorPrize key={month + ":" + data.self_id} month={month} days={days} data={data} />
        : <ConsultantPrize key={data.self_id} userId={data.self_id} days={days} />)}
      <SalesRanking key={month} data={data} month={month} />
      {data.manager ? <div className="space-y-4"><h3 className="font-bold">Metas individuais</h3>
        <p className="text-xs text-slate-400">As metas individuais podem ser diferentes da divisão da loja. Editá-las não altera o resultado nem a meta da loja.</p>
        {data.individual_user_ids.filter(id => id !== data.self_id).map(id => <details key={id} className="border border-slate-700 rounded p-3">
          <summary className="cursor-pointer font-semibold">{id === data.self_id ? `${names[id]} (minha meta)` : names[id] || "Consultora"}</summary>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <Button type="button" disabled={busy} onClick={() => editIndividualGoal(id)}>Editar meta individual</Button>
            <span className="text-xs text-slate-400">{data.individual_custom[id] ? "Meta individual definida" : "Valor inicial pela divisão da loja"}</span>
          </div>
          <GoalsTable products={data.products} targets={data.individual_targets[id] || {}} produced={data.by_user[id] || {}} days={days} onCorrect={product => { setSale(null); setCorrection({month, product, owner: id}); }} />
        </details>)}
      </div> : null}
      <p className="text-xs text-slate-400">Diária necessária = falta ÷ dias restantes; fica zero quando a meta é atingida. Projeção = produzido ÷ dias decorridos × dias do mês. Seguro e Super Conta não entram no total em R$.</p>
      <details><summary className="cursor-pointer font-semibold">Lançamentos ({data.entries.length})</summary><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr>{["Data","Consultora","Produto","Produção", ""].map((h,i) => <th className="p-2 text-left" key={i}>{h}</th>)}</tr></thead><tbody>{data.entries.map(e => { const p=data.products.find(p => p.id===e.product); return <tr key={e.id} className="border-t border-slate-800"><td className="p-2">{e.date.split("-").reverse().join("/")}</td><td className="p-2">{names[e.user_id] || "Consultora removida"}</td><td className="p-2">{p.name}</td><td className="p-2">{display(e.amount,p.unit)}</td><td><Button disabled={busy} onClick={() => startSale(e)}>Corrigir</Button></td></tr>; })}</tbody></table></div></details>
    </> : <Button onClick={load}>Tentar novamente</Button>}
  </Card>;
}

function pickStoreFocus(products, targets, totals, days) {
  if (!days.passed || !days.remaining) return null;
  let focus = null;
  for (const product of products) {
    const target = targets[product.id] || 0;
    const actual = totals[product.id] || 0;
    if (product.id === "total" || target <= 0 || actual >= target) continue;
    const ratio = actual / days.passed * days.total / target;
    if (ratio >= 1 || (focus && ratio >= focus.ratio)) continue;
    focus = { product, ratio, daily: (target - actual) / days.remaining };
  }
  return focus;
}

function StoreFocus({data, days}) {
  const focus = pickStoreFocus(data.products, data.targets, data.totals, days);
  if (!focus) return null;
  const {product, daily} = focus;
  const needed = product.unit === "count" ? Math.ceil(daily) : daily;
  return <aside className="my-2 rounded-lg border border-pink-500/40 bg-pink-500/10 p-3 text-sm">
    <strong className="text-pink-300">🎯 Foco de hoje: {product.name}</strong>
    <p>Precisa de {display(needed, product.unit)}{product.unit === "count" ? (needed === 1 ? " unidade" : " unidades") : ""} por dia útil pra bater a meta do mês.</p>
    <Link className="mt-2 inline-block rounded-md border border-pink-500/50 px-3 py-1 text-pink-300 underline" to={"/contacts?" + new URLSearchParams({ radar: "1", product: product.name }).toString()}>
      Ver propostas paradas de {product.name}
    </Link>
  </aside>;
}

function PaceCard({name, products, targets, totals, remaining}) {
  const money = products.filter(p => p.unit === "money");
  const goal = money.reduce((sum,p) => sum + (targets?.[p.id] || 0), 0);
  const actual = money.reduce((sum,p) => sum + (totals?.[p.id] || 0), 0);
  const left = Math.max(0, goal - actual);
  return <div className="rounded-lg border border-slate-700 px-3 py-2 text-sm">
    <div className="font-semibold">{name}</div>
    <div className="text-pink-300">{!goal ? "Sem meta em R$" : !left ? "Meta atingida!" : !remaining ? "Mês encerrado" : display(Math.ceil(left / remaining), "money") + " por dia"}</div>
    {goal > 0 && <div className="text-xs text-slate-400">Falta {display(left, "money")}</div>}
  </div>;
}

function ligaPrize(mix, target, team) {
  const rate = mix >= target ? 0.0025 : mix * 10 >= target * 9 ? 0.0015 : 0;
  return Math.min(1500000, Math.round(team / 2 + mix * rate));
}

function ligaMinimum(desired, current, prizeAt) {
  if (prizeAt(current) >= desired) return current;
  let low = current;
  let high = Math.max(current, 100000000000);
  if (prizeAt(high) < desired) return null;
  while (high - low > 1) {
    const mid = Math.floor((low + high) / 2);
    if (prizeAt(mid) >= desired) high = mid;
    else low = mid;
  }
  return high;
}

const consultantBands = {
  "CNC": [[30000,60],[35000,90],[40000,120],[45000,150],[50000,180],[55000,210],[60000,240],[65000,270],[75000,300],[85000,400],[100000,500]],
  "CNC (consultora nova)": [[15000,30],[30000,60],[35000,90],[40000,120],[45000,150],[50000,180],[55000,210],[60000,240],[65000,270],[75000,300],[85000,400],[100000,500]],
  "Consignado Bmg": [[5000,10],[20000,50],[30000,75],[40000,100]],
  "Consignado Privado": [[0,100],[40000,150],[60000,200]],
  "Cartões": [[0,100]],
  "FGTS": [[0,100]],
};
function consultantRequired(gap, bands) {
  if (gap <= 0) return 0;
  for (let i = 0; i < bands.length; i++) {
    const [minimum, rate] = bands[i];
    const needed = Math.max(minimum * 100, Math.ceil(gap * 10000 / rate));
    if (i === bands.length - 1 || needed < bands[i + 1][0] * 100) return needed;
  }
  return 0;
}

function ConsultantPrize({userId, days}) {
  const storageKey = "liga:consultora:2026-11:" + userId;
  const [fields, setFields] = useState(() => {
    try {
      const saved = userId && JSON.parse(localStorage.getItem(storageKey));
      if (Array.isArray(saved) && saved.length === 4 && saved.every(v => typeof v === "string") && Object.keys(consultantBands).includes(saved[3])) return saved;
    } catch {}
    return ["", "", "", "CNC"];
  });
  const [status, setStatus] = useState("");
  function update(i, value) {
    const next = fields.map((v, j) => i === j ? value : v);
    setFields(next);
    try {
      if (userId) localStorage.setItem(storageKey, JSON.stringify(next));
    } catch { setStatus("Não foi possível salvar neste navegador."); }
  }
  const [goal, actual, other] = fields.slice(0, 3).map(raw => {
    const value = raw.trim().replace(/^R\$\s*/, "");
    if (!/^(?:[0-9]+|[0-9]{1,3}(?:[.][0-9]{3})+)(?:,[0-9]{1,2})?$/.test(value)) return null;
    const cents = Math.round(Number(value.replace(/[.]/g, "").replace(",", ".")) * 100);
    return Number.isSafeInteger(cents) && cents <= 100000000000 ? cents : null;
  });
  const product = fields[3];
  const valid = goal > 0 && goal <= 1200000 && actual !== null && other !== null;
  const needed = valid ? consultantRequired(goal - other, consultantBands[product]) : 0;
  const left = Math.max(0, needed - actual);
  return <details className="rounded-xl border border-pink-500/30 p-3">
    <summary className="cursor-pointer font-semibold text-pink-300">Simular meu prêmio LIGA — Consultora</summary>
    <p className="my-2 text-xs text-slate-400">Escolha um produto para planejar quanto produzir. Use a produção líquida elegível de novembro/2026. A simulação fica neste navegador.</p>
    <div className="grid gap-2 sm:grid-cols-2">
      {["Quanto quero ganhar em prêmio (R$)", "Já produzido no produto escolhido (R$)", "Prêmio estimado de outras fontes (R$)"].map((label, i) => <label key={label} className="text-sm">{label}
        <input className="mt-1 w-full rounded border border-slate-700 bg-slate-900 p-2" inputMode="decimal" placeholder="0,00" value={fields[i]} onChange={e => update(i, e.target.value)} />
      </label>)}
      <label className="text-sm">Produto que quero produzir
        <select className="mt-1 w-full rounded border border-slate-700 bg-slate-900 p-2" value={product} onChange={e => update(3, e.target.value)}>
          {Object.keys(consultantBands).map(name => <option key={name}>{name}</option>)}
        </select>
      </label>
    </div>
    {goal > 1200000 ? <p>O teto da consultora é R$ 12.000,00.</p> : valid ? <div className="mt-2 text-sm" role="status">
      <p>Produção mínima em {product}: <strong>{display(needed, "money")}</strong></p>
      <p>Falta produzir: <strong>{display(left, "money")}</strong></p>
      {days.remaining > 0 && <p>Por dia útil restante: <strong>{display(Math.ceil(left / days.remaining), "money")}</strong></p>}
    </div> : <p className="mt-2 text-xs text-slate-400">Preencha os valores. Use 0 se ainda não produziu ou se não há outros prêmios estimados.</p>}
    <p className="mt-2 text-xs text-slate-400">Estimativa pelas faixas de produção. Informe bônus e outros produtos no campo de outras fontes. Elegibilidade, ajustes de faixa e descontos do BI podem mudar o resultado.</p>
    {status && <p role="status">{status}</p>}
  </details>;
}

function SupervisorPrize({month, days, data}) {
  const storageKey = data.self_id ? "liga:supervisora:" + month + ":" + data.self_id : null;
  const [fields, setFields] = useState(() => {
    let goals = ["", ""];
    try {
      const saved = storageKey && JSON.parse(localStorage.getItem(storageKey));
      if (Array.isArray(saved) && saved.length === 2 && saved.every(value => typeof value === "string")) goals = saved;
    } catch {}
    return [...goals, "", "", "300000"];
  });
  const [goalSaveFailed, setGoalSaveFailed] = useState(false);
  function updateField(i, value) {
    const next = fields.map((previous, index) => index === i ? value : previous);
    setFields(next);
    if (i === 2) setDailyStatus("");
    if (i > 1 || !storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(next.slice(0, 2)));
      setGoalSaveFailed(false);
    } catch { setGoalSaveFailed(true); }
  }
  const labels = ["Quanto quero ganhar em prêmio (R$)", "Meta mensal do MIX (R$)", "MIX líquido já produzido (R$)", "Prêmios das consultoras (R$) — ajuste opcional", "Cenário: MIX líquido no mês (R$)"];
  const values = fields.map(raw => {
    const value = raw.trim().replace(/^R\$\s*/, "");
    if (!/^(?:[0-9]+|[0-9]{1,3}(?:[.][0-9]{3})+)(?:,[0-9]{1,2})?$/.test(value)) return null;
    const cents = Math.round(Number(value.replace(/[.]/g, "").replace(",", ".")) * 100);
    return Number.isSafeInteger(cents) && cents <= 100000000000 ? cents : null;
  });
  const [desired, target, actual, manualTeamPrize, scenario] = values;
  const [dailyStatus, setDailyStatus] = useState("");
  function useDailyProduction() {
    const amounts = ["cnc", "consignado", "clt", "antecipacao"].map(key => data.totals?.[key]);
    const total = amounts.reduce((sum, amount) => sum + (amount ?? 0), 0);
    if (!amounts.some(Number.isSafeInteger) || !Number.isSafeInteger(total) || total < 0 || total > 100000000000) {
      setDailyStatus("Não foi possível puxar o valor da Diária. Você pode preenchê-lo manualmente.");
      return;
    }
    const formatted = (total / 100).toLocaleString("pt-BR", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    setFields(current => current.map((value, i) => i === 2 ? formatted : value));
    setDailyStatus("Produção da Diária preenchida. Ajuste se o valor do BI for diferente.");
  }
  const pace = days.passed ? days.total / days.passed : 0;
  const ids = (data.individual_user_ids || []).filter(id => id !== data.self_id);
  const teamAt = (factor, fgtsFactor = factor) => ids.reduce((sum, id) => {
    const totals = data.by_user?.[id] || {};
    const products = [["cnc", "CNC"], ["consignado", "Consignado Bmg"], ["fgts", "FGTS"]];
    const prize = products.reduce((total, [key, name]) => {
      const production = (totals[key] || 0) * (key === "fgts" ? fgtsFactor : factor);
      const band = consultantBands[name].filter(([min]) => production >= min * 100).pop();
      return total + production * (band?.[1] || 0) / 10000;
    }, 0);
    return sum + Math.min(1200000, Math.round(prize));
  }, 0);
  const teamPrize = fields[3].trim() ? manualTeamPrize : teamAt(pace);
  const projectedMix = actual === null ? null : Math.round(actual * pace);
  const canProject = days.passed > 0 && target > 0 && projectedMix !== null && teamPrize !== null;
  const projectedPrize = canProject ? ligaPrize(projectedMix, target, teamPrize) : null;
  const hasMixSales = ids.some(id => {
    const totals = data.by_user?.[id];
    return (totals?.cnc || 0) > 0 || (totals?.consignado || 0) > 0;
  });
  const canSimulate = target > 0 && actual > 0 && hasMixSales && days.passed > 0;
  const prizeAt = mix => ligaPrize(mix, target, teamAt(mix / actual, pace));
  const scenarioPrize = canSimulate && scenario >= actual ? prizeAt(scenario) : null;
  const valid = canSimulate && desired > 0 && desired <= 1500000;
  const needed = valid ? ligaMinimum(desired, actual, prizeAt) : null;
  const left = needed === null ? null : Math.max(0, needed - actual);
  return <details className="rounded-xl border border-pink-500/30 p-3">
    <summary className="cursor-pointer font-semibold text-pink-300">Simular meu prêmio LIGA — Supervisora</summary>
    <div className="mt-3 rounded-lg border border-pink-500/30 bg-pink-500/10 p-3 text-sm" role="status">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p>Se a loja mantiver o ritmo atual:</p>
          <p className="text-lg font-semibold text-pink-300">Prêmio estimado no ritmo atual: {projectedPrize === null ? "—" : display(projectedPrize, "money")}</p>
        </div>
        <div>
          {scenarioPrize === null ? <p>Para simular o cenário, informe a meta do MIX, a produção atual e o fechamento desejado.</p> : <>
            <p>Se a loja fechar o mês com <strong>{display(scenario, "money")}</strong> em vendas elegíveis:</p>
            <p className="text-lg font-semibold text-pink-300">Prêmio estimado no cenário: {display(scenarioPrize, "money")}</p>
            {desired > 0 && desired <= 1500000 && <p>{scenarioPrize >= desired
              ? "Nesse cenário, você alcança seu objetivo de " + display(desired, "money") + " em prêmio."
              : "Pra chegar a " + display(desired, "money") + " de prêmio, faltam " + display(desired - scenarioPrize, "money") + " de prêmio."}</p>}
          </>}
        </div>
      </div>
      {desired > 1500000 && <p className="text-pink-300">O objetivo passa do teto de R$ 15.000,00 para supervisão.</p>}
    </div>
    <p className="my-2 text-xs text-slate-400">Supervisora · Cartilha de novembro/2026. Informe a meta e o MIX líquido já produzido. Deixe o ajuste opcional em branco para usar a estimativa automática da equipe.</p>
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" onClick={useDailyProduction}>Usar dados da Diária</Button>
      <span className="text-xs text-slate-400" role="status">{dailyStatus || "Soma CNC, Consignado, CLT e Antecipação registrados no mês. Confira o MIX elegível no BI."}</span>
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      {labels.map((label, i) => <label key={label} className="text-sm">{label}
        <input className="mt-1 w-full rounded border border-slate-700 bg-slate-900 p-2" inputMode="decimal" placeholder="0,00" value={fields[i]} onChange={event => updateField(i, event.target.value)} />
      </label>)}
    </div>
    {storageKey && <p className="mt-2 text-xs text-slate-400" role="status">{goalSaveFailed
      ? "Não foi possível guardar suas metas neste navegador."
      : "Seu prêmio desejado e a meta mensal do MIX ficam guardados neste navegador."}</p>}
    <details className="mt-3 rounded-lg border border-slate-700 p-2">
      <summary className="cursor-pointer text-sm text-pink-300">Ver detalhes</summary>
    <div className="mt-2 text-sm" role="status">
      <p>Prêmios projetados das consultoras ({fields[3].trim() ? "ajuste manual" : "estimativa automática"}): <strong>{teamPrize === null ? "—" : display(teamPrize, "money")}</strong></p>
    </div>
    {desired <= 1500000 && (
      needed !== null ? <div className="mt-2 text-sm" role="status">
        <p>MIX estimado para atingir seu prêmio: <strong>{display(needed, "money")}</strong></p>
        <p>Falta produzir: <strong>{display(left, "money")}</strong></p>
        {days.remaining > 0 && <p>Por dia útil restante: <strong>{display(Math.ceil(left / days.remaining), "money")}</strong></p>}
      </div> : <p className="mt-2 text-xs text-slate-400">Para estimar quanto falta, informe o prêmio desejado, a meta e o MIX já produzido. É preciso ter vendas de CNC ou Consignado registradas na equipe.</p>)}
    <p className="mt-2 text-xs text-slate-400">O cenário mantém a proporção atual de CNC e Consignado de cada consultora e recalcula as faixas conforme o MIX cresce. O FGTS fica na projeção atual. O ajuste manual vale só para a projeção no ritmo atual. A estimativa não confirma elegibilidade, bônus extras ou descontos do BI.</p>
    </details>
  </details>;
}

function CopyDailyTargets({data, month, days}) {
  const [status, setStatus] = useState("");
  if (month !== data.today.slice(0, 7) || !days.todayIsWorkday || !days.remaining) return null;
  const products = data.products.filter(p => p.id !== "total" && (data.targets[p.id] || 0) > 0);
  if (!products.length) return null;
  async function copy() {
    const title = "Meta diária por produto — " + data.today.split("-").reverse().join("/");
    const lines = products.map(p => {
      const daily = Math.max(0, data.targets[p.id] - (data.totals[p.id] || 0)) / days.remaining;
      const value = p.unit === "count" ? Math.ceil(daily) : daily;
      return p.name + " — " + display(value, p.unit) + (p.unit === "count" ? (value === 1 ? " unidade" : " unidades") : "");
    });
    try {
      await navigator.clipboard.writeText([title, ...lines].join("\n"));
      setStatus("Meta diária copiada!");
    } catch {
      setStatus("Não foi possível copiar a meta diária.");
    }
  }
  return <div className="flex flex-wrap items-center gap-2">
    <Button type="button" variant="outline" onClick={copy}>Copiar diária por produto</Button>
    <span role="status" className="text-xs text-slate-400">{status}</span>
  </div>;
}

function DailyClosing({data, month, names}) {
  const [status, setStatus] = useState("");
  const current = month === data.today.slice(0, 7);
  async function copy() {
    const entries = data.entries.filter(e => e.date === data.today);
    const money = data.products.filter(p => p.unit === "money");
    const quantity = data.products.filter(p => p.unit === "count");
    function line(name, owner, targets, totals) {
      const sales = owner ? entries.filter(e => e.user_id === owner) : entries;
      const sum = products => sales.reduce((n,e) => n + (products.some(p => p.id === e.product) ? e.amount : 0), 0);
      const goal = money.reduce((n,p) => n + (targets?.[p.id] || 0), 0);
      const actual = money.reduce((n,p) => n + (totals?.[p.id] || 0), 0);
      return [name + ": " + display(sum(money), "money"), ...quantity.map(p => p.name + ": " + sum([p])), goal ? "Falta no mês: " + display(Math.max(0,goal-actual), "money") : "Sem meta em R$"].join(" · ");
    }
    const lines = [ "Fechamento de " + data.today.split("-").reverse().join("/"),
      line(data.manager ? "Loja" : "Minha produção", null, data.targets, data.totals) ];
    if (data.manager) for (const id of new Set([...data.individual_user_ids, ...entries.map(e => e.user_id)])) {
      if (id === data.self_id) continue;
      lines.push(line(names[id] || "Consultora", id, data.individual_targets[id], data.by_user[id]));
    }
    try { await navigator.clipboard.writeText(lines.join("\n")); setStatus("Fechamento copiado!"); }
    catch { setStatus("Não foi possível copiar o fechamento."); }
  }
  return <div className="flex flex-wrap items-center gap-2">
    <Button type="button" variant="outline" disabled={!current} onClick={copy}>Copiar fechamento de hoje</Button>
    <span role="status" className="text-xs text-slate-400">{current ? status : "Selecione o mês atual para copiar."}</span>
  </div>;
}

function TodayProduction({name, data, owner, targets, totals, remaining, todayIsWorkday}) {
  const today = {};
  for (const e of data.entries) {
    if (e.date === data.today && (!owner || e.user_id === owner)) today[e.product] = (today[e.product] || 0) + e.amount;
  }
  const money = data.products.filter(p => p.unit === "money");
  const sum = values => money.reduce((n,p) => n + (values?.[p.id] || 0), 0);
  const goal = sum(targets);
  const producedToday = sum(today);
  const daily = goal && remaining && todayIsWorkday
    ? Math.ceil(Math.max(0, goal - (sum(totals) - producedToday)) / remaining)
    : null;
  const dailyLeft = daily === null ? null : Math.max(0, daily - producedToday);

  return <div className="rounded-lg border border-slate-700 px-3 py-2 text-sm">
    <strong>{name}</strong>
    <p className="text-pink-300">Produzido hoje: {display(producedToday, "money")}</p>
    <p className="text-xs">{data.products.filter(p => p.unit === "count").map(p => p.name + ": " + (today[p.id] || 0)).join(" · ")}</p>
    {daily > 0 ? <div className="my-2 space-y-1 text-xs">
      <p>Meta do dia: {display(daily, "money")}</p>
      <div role="progressbar" aria-label={name + " — progresso da meta de hoje"} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.max(0, Math.min(100, Math.floor(producedToday / daily * 100)))} className="h-2 overflow-hidden rounded-full bg-slate-700">
        <div className="h-full rounded-full bg-pink-500" style={{width: Math.max(0, Math.min(100, producedToday / daily * 100)) + "%"}} />
      </div>
      {dailyLeft > 0 ? <p className="text-pink-300">Faltam {display(dailyLeft, "money")} pra meta de hoje</p> :
        <div role="status" className="flex items-center gap-2 text-pink-300">
      <style>{`
        @keyframes ranking-cat-hop {
          0%, 50%, 100% { transform: translateY(0); }
          25%, 75% { transform: translateY(-7px) rotate(-8deg); }
        }
        .ranking-cat-hop { animation: ranking-cat-hop 1.9s ease-in-out both; }
        @media (prefers-reduced-motion: reduce) {
          .ranking-cat-hop { animation: none; }
        }
      `}</style>
      <svg className="ranking-cat-hop" aria-hidden="true" width="44" height="36" viewBox="0 0 44 36">
        <path d="M10 20 10 6l9 7h6l9-7v14c0 8-5 12-12 12s-12-4-12-12Z" fill="#f472b6" stroke="#c084fc" strokeWidth="2"/>
        <circle cx="17" cy="21" r="1.5" fill="#301442"/>
        <circle cx="27" cy="21" r="1.5" fill="#301442"/>
        <path d="M19 25q1.5 3 3 0 1.5 3 3 0M2 15l3 2m33 10 4-2M20 5l1-3" fill="none" stroke="#fde68a" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
          <strong>Meta do dia batida! 🎉</strong>
        </div>}
    </div> : daily === 0 ? <p className="text-xs">Meta mensal já atingida</p> : !todayIsWorkday ? <p className="text-xs">Sem meta diária hoje: feriado ou fim de semana.</p> : null}
    <p className="text-xs text-slate-400">{goal ? "Falta no mês: " + display(Math.max(0, goal - sum(totals)), "money") : "Sem meta mensal em R$"}</p>
  </div>;
}
